import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Tag, Grid, Zap, ArrowRight, CheckCircle, XCircle } from 'lucide-react';

interface DigitalAsset {
  _id: string;
  title: string;
  description: string;
  price: number;
  discountPrice?: number;
  category: string;
  status: string;
  previewUrl?: string;
}

export default function DigitalAssetsTable() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [assets] = useState<DigitalAsset[]>([
    {
      _id: '1',
      title: 'Modern UI Kit',
      description: 'Figma UI components library with 50+ screens',
      price: 49.99,
      discountPrice: 39.99,
      category: 'Figma',
      status: 'active',
      previewUrl: 'https://via.placeholder.com/80'
    },
    {
      _id: '2',
      title: 'Vector Icons Pack',
      description: '500+ customizable SVG icons',
      price: 29.99,
      category: 'icon',
      status: 'active',
      previewUrl: 'https://via.placeholder.com/80'
    },
  ]);

  const filteredAssets = assets.filter(asset =>
    asset.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'Figma': 'bg-purple-100 text-purple-800',
      'icon': 'bg-blue-100 text-blue-800',
      'graphic': 'bg-pink-100 text-pink-800',
      'UI/UX': 'bg-orange-100 text-orange-800',
      'Web Design': 'bg-green-100 text-green-800',
      'default': 'bg-navy-100 text-navy-800'
    };
    return colors[category] || colors.default;
  };

  const handleAssetClick = (id: string) => {
    navigate(`/assets/details/${id}`);
  };

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <div className="flex items-center space-x-2">
          <div className="p-2 bg-navy-900 rounded-lg">
            <Zap size={24} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-center text-navy-900">Digital Assets</h1>
        </div>
        
        <div className="relative w-full sm:w-96">
          <input
            type="text"
            placeholder="Search assets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border-2 border-navy-100 rounded-xl focus:ring-4 focus:ring-navy-100 focus:border-navy-500 transition-all"
          />
          <Search size={20} className="absolute left-4 top-3.5 text-navy-400" />
        </div>
      </div>

      <div className="border-2 border-navy-100 rounded-xl overflow-hidden shadow-lg">
        <div className="hidden sm:grid grid-cols-12 bg-navy-50 p-4 text-sm font-medium text-navy-900">
          <div className="col-span-6">Asset</div>
          <div className="col-span-2">Category</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2 text-right">Price</div>
        </div>

        {filteredAssets.map((asset) => (
          <div
            key={asset._id}
            onClick={() => handleAssetClick(asset._id)}
            className="group grid sm:grid-cols-12 gap-4 p-4 border-b border-navy-100 hover:bg-white cursor-pointer transition-all duration-300 hover:shadow-[0_8px_30px_rgb(0,31,63,0.05)]"
          >
            {/* Asset Preview & Info */}
            <div className="col-span-6 flex items-center space-x-4">
              <div className="w-20 h-20 bg-navy-100 rounded-lg overflow-hidden flex items-center justify-center">
                {asset.previewUrl ? (
                  <img src={asset.previewUrl} alt={asset.title} className="object-cover w-full h-full" />
                ) : (
                  <Grid size={24} className="text-navy-400" />
                )}
              </div>
              <div>
                <div className="font-medium text-navy-900 group-hover:text-navy-700 transition-colors">
                  {asset.title}
                  <ArrowRight size={16} className="ml-2 inline-block opacity-0 group-hover:opacity-100 transition-all" />
                </div>
                <div className="text-sm text-navy-600 line-clamp-1">{asset.description}</div>
              </div>
            </div>
            
            {/* Category */}
            <div className="col-span-2 flex items-center">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(asset.category)}`}>
                {asset.category}
              </span>
            </div>
            
            {/* Status */}
            <div className="col-span-2 flex items-center space-x-2">
              {asset.status === 'active' ? (
                <CheckCircle size={16} className="text-green-500" />
              ) : (
                <XCircle size={16} className="text-red-500" />
              )}
              <span className={`text-sm font-medium ${
                asset.status === 'active' ? 'text-green-700' : 'text-red-700'
              }`}>
                {asset.status.charAt(0).toUpperCase() + asset.status.slice(1)}
              </span>
            </div>
            
            {/* Price */}
            <div className="col-span-2 text-right">
              <div className="space-y-1">
                {asset.discountPrice ? (
                  <>
                    <span className="block text-lg font-bold text-navy-900">
                      ${asset.discountPrice.toFixed(2)}
                    </span>
                    <span className="block text-sm line-through text-navy-400">
                      ${asset.price.toFixed(2)}
                    </span>
                  </>
                ) : (
                  <span className="block text-lg font-bold text-navy-900">
                    ${asset.price.toFixed(2)}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
